import { useState, useMemo, useCallback, useImperativeHandle, ForwardedRef, useRef, useEffect } from 'react';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import storage from 'utils/sessionStorage';
import appointmentApis from 'features/appointment/services/apis';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { getMessageError, IResponseDataBody } from 'services/response';
import toast from 'utils/toast';
import { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import { DATE_FORMAT } from 'features/appointment/utils/format';
import { CALENDAR_VIEW_TYPE } from 'features/appointment/services/constants';
import { quickBookingV2Ref } from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/Body/Modal/QuickBookingV2';
import { maskPhone } from 'utils/formatPhone';
import userActions from 'features/user/services/actions';
import userSelectors from 'features/user/services/selectors';
import { useAppDispatch } from 'store/hooks';
import { ICallerIdHistoryItem, IApiCallerIdHistoryItem, ISummaryData, ICallerIdHistoryRef } from './types';

const CALLER_ID_TYPE = 'CALLER_ID_HISTORY';

export const useModalCallerIdHistory = (ref: ForwardedRef<ICallerIdHistoryRef>) => {
  const dispatch = useAppDispatch();
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<ICallerIdHistoryItem[]>([]);
  const [summary, setSummary] = useState<ISummaryData>({
    smsSent: 0,
    callIn: 0,
    booked: 0,
    newCus: 0,
    regular: 0,
    vipCus: 0,
  });
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const abortControllerSummaryRef = useRef<AbortController | null>(null);
  const abortControllerHistoryRef = useRef<AbortController | null>(null);
  const prevDateRangeRef = useRef<{ start: string; end: string } | null>(null);

  const modalConfirm = useModalConfirmRef();
  const navigate = useNavigate();
  const location = useLocation();
  const setLoadingPage = useSetLoadingPage();

  const params = userSelectors.getCallerIdParams()?.[CALLER_ID_TYPE];
  const dateRange = useMemo(() => {
    if (!params) {
      const now = moment();
      return { start: now.format(DATE_FORMAT), end: now.format(DATE_FORMAT) };
    }
    return { start: params.startTime || '', end: params.endTime || '' };
  }, [params]);

  const fetchSummary = useCallback(async (startDate?: string, endDate?: string) => {
    const start = startDate || dateRange.start;
    const end = endDate || dateRange.end;
    if (!start || !end) return;

    if (abortControllerSummaryRef.current) {
      abortControllerSummaryRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerSummaryRef.current = abortController;

    setLoadingSummary(true);
    try {
      const summaryRes = await appointmentApis.getSummaryCallerId({
        startDate: start,
        endDate: end,
      }, { signal: abortController.signal });

      if (abortController.signal.aborted) return;

      if (summaryRes.data?.data) {
        const summaryData = summaryRes.data.data;
        setSummary({
          smsSent: summaryData.sendSmsCount || 0,
          callIn: summaryData.totalCaller || 0,
          booked: summaryData.bookedCount || 0,
          newCus: summaryData.newCustomerCount || 0,
          regular: summaryData.oldCustomerCount || 0,
          vipCus: summaryData.vipCusCount || 0,
        });
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return;
      }
      const msg = getMessageError(error);
      toast.error(msg);
    } finally {
      if (!abortController.signal.aborted) {
        setLoadingSummary(false);
      }
    }
  }, [dateRange.start, dateRange.end]);

  const fetchHistory = useCallback(async (page: number, pageSize: number, startDate?: string, endDate?: string) => {
    const start = startDate || dateRange.start;
    const end = endDate || dateRange.end;

    if (!start || !end) return;

    if (abortControllerHistoryRef.current) {
      abortControllerHistoryRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerHistoryRef.current = abortController;

    setLoadingHistory(true);
    setData([]);
    try {
      const historyRes = await appointmentApis.getHistoryCallerId({
        startDate: start,
        endDate: end,
        page,
        pageSize,
      }, { signal: abortController.signal });

      if (abortController.signal.aborted) return;

      if (historyRes.data?.data) {
        const historyData = historyRes.data.data;
        const content = (historyData.content as IApiCallerIdHistoryItem[]) || [];

        const mappedData: ICallerIdHistoryItem[] = content.map((item, index) => ({
          id: item.id || item.customerId || `caller-${index}-${Date.now()}`,
          dateTime: item.date,
          time: item.time,
          customerName: item.customerName,
          phone: item.customerPhone,
          total: item.customerType,
          customerId: item.customerId,
          booked: item.booked,
          sentSms: item.sentSms,
          customerType: item.customerType,
          numberVisit: item.numberVisit || 0,
          callerId: item.callerId || '',
        }));

        setData(mappedData);
        setPagination(prev => ({
          ...prev,
          total: historyData.totalElements || 0,
        }));
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return;
      }
      const msg = getMessageError(error);
      toast.error(msg);
    } finally {
      if (!abortController.signal.aborted) {
        setLoadingHistory(false);
      }
    }
  }, [dateRange.start, dateRange.end]);

  const handleChangePage = useCallback((page: number, pageSize?: number) => {
    const nextPageSize = pageSize || pagination.pageSize;
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: nextPageSize,
    }));
    if (visible && dateRange.start && dateRange.end) {
      fetchHistory(page, nextPageSize);
    }
  }, [visible, dateRange.start, dateRange.end, pagination.pageSize, fetchHistory]);

  const handleSetVisible = useCallback((value: boolean) => {
    setVisible(value);
    if (!value) {
      if (abortControllerSummaryRef.current) {
        abortControllerSummaryRef.current.abort();
      }
      if (abortControllerHistoryRef.current) {
        abortControllerHistoryRef.current.abort();
      }
    }
  }, []);

  const handleSendSMS = async (phone: string, callerId: string) => {
    setLoadingPage(true);
    try {
      const res: IResponseDataBody<boolean> = await appointmentApis.aptSendSMS(phone, callerId);
      if (res.data.data) {
        toast.success('SMS sent successfully');
      }
    } catch (error) {
      const msg = getMessageError(error);
      toast.error(msg);
    } finally {
      setLoadingPage(false);
    }
  };

  const onSendSMS = (phone: string, callerId: string) => {
    modalConfirm.current?.open(`Do you want to send SMS to ${maskPhone(phone)}?`, () => handleSendSMS(phone, callerId));
  };

  const onBook = (item: ICallerIdHistoryItem) => {
    handleSetVisible(false);
    if (item.phone) {
      quickBookingV2Ref.current?.openWithCaller({
        phone: item.phone,
        name: item.customerName || '',
        id: item.id || '',
        customerType: item.customerType,
        numberVisit: item.numberVisit,
        callerId: item.callerId,
      });
      if (!location.pathname.includes('/appointments')) {
        setTimeout(() => {
          navigate(`/store/${storage.shop_id.get()}/appointments`);
        }, 10);
      }
    }
  };

  const handleOpen = useCallback(() => {
    const now = moment();
    const today = now.format(DATE_FORMAT);

    dispatch(userActions.setCallerIdParams({
      [CALLER_ID_TYPE]: {
        startTime: today,
        endTime: today,
      },
    }));
    dispatch(userActions.setCallerIdViewType({
      [CALLER_ID_TYPE]: CALENDAR_VIEW_TYPE.DAY_VIEW,
    }));

    const resetPagination = { current: 1, pageSize: pagination.pageSize, total: 0 };
    setPagination(resetPagination);
    prevDateRangeRef.current = { start: today, end: today };

    handleSetVisible(true);
    fetchSummary(today, today);
    fetchHistory(1, resetPagination.pageSize, today, today);
  }, [dispatch, pagination.pageSize, fetchSummary, fetchHistory, handleSetVisible]);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
  }), [handleOpen]);

  const handleResetPagination = useCallback(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, []);

  useEffect(() => {
    if (!visible || !dateRange.start || !dateRange.end) return;

    setPagination(prev => {
      if (prev.current === 1) return prev;
      return { ...prev, current: 1 };
    });

    fetchSummary();
    fetchHistory(1, pagination.pageSize);
  }, [visible, dateRange.start, dateRange.end, fetchSummary, fetchHistory, pagination.pageSize]);

  return {
    visible,
    setVisible: handleSetVisible,
    data,
    summary,
    loadingSummary,
    loadingHistory,
    pagination,
    handleChangePage,
    onSendSMS,
    onBook,
    modalConfirm,
    handleResetPagination,
    CALLER_ID_TYPE,
  };
};
